import { useCallback, useEffect, useState } from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import { useTheme } from "@/components/providers/ThemeProvider";
import { cn } from "@/lib/utils";

interface ThemeToggleProps {
  className?: string;
  /** When true, render a wider button with text label (for mobile menu). */
  withLabel?: boolean;
}

/**
 * Tri-state theme toggle: light → dark → system → light.
 * Uses the local ThemeProvider; FOUC bootstrap in index.html handles
 * the initial class so reloading in dark mode never flashes light.
 */
export function ThemeToggle({ className, withLabel = false }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();
  // Until mount, the ThemeProvider has not yet reconciled the stored /
  // system preference, so the live `theme` value reflects the default
  // (`"light"`) — which would paint an incorrect Sun icon on first
  // commit for users who prefer Dark or System. Render a neutral
  // Monitor placeholder with stable dimensions and accessible label
  // until reconciliation runs.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const cycle = useCallback(() => {
    setTheme(theme === "light" ? "dark" : theme === "dark" ? "system" : "light");
  }, [theme, setTheme]);

  const Icon = !mounted
    ? Monitor
    : theme === "dark"
    ? Moon
    : theme === "system"
    ? Monitor
    : Sun;
  const label = !mounted
    ? "Theme"
    : theme === "dark"
    ? "Switch to system theme"
    : theme === "system"
    ? "Switch to light theme"
    : "Switch to dark theme";
  const visible = !mounted
    ? ""
    : theme === "dark"
    ? "Dark"
    : theme === "system"
    ? "System"
    : "Light";

  return (
    <button
      type="button"
      onClick={cycle}
      disabled={!mounted}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        withLabel ? "h-12 w-full gap-2 px-3 text-sm font-medium" : "h-10 w-10",
        className
      )}
    >
      <Icon className="h-5 w-5" />
      {withLabel && <span>{mounted ? `Theme: ${visible}` : "Theme"}</span>}
    </button>
  );
}
